import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowLeft, ArrowRight, BriefcaseBusiness, LayoutGrid, MonitorCog, X, type LucideIcon } from "lucide-react";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent, type Modifier } from "@dnd-kit/core";
import { arrayMove, horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LogoDisc } from "@/components/ui/logo-plate";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useRecentProjects } from "@/hooks/useRecentProjects";
import { useTranslation } from "@/hooks/useTranslation";
import { projectsListQuery } from "@/lib/projectsQuery";
import { readProjectsSnapshot } from "@/lib/projectsSnapshot";
import { getRecentProjectIds } from "@/lib/recentProjects";
import { toast } from "@/hooks/use-toast";
import { isApplePlatform, spaceShortcut } from "@/lib/shellFlat";

const MAX_SPACES = 5;

/* The open spaces, front first, per device: project ids, and "company" /
   "platform" for the two settings areas. At most five. Opening something not
   in the row puts it first and closes the fifth; opening one already in the
   row leaves the row alone (Andrew, 2026-11-02). Null until first seeded from
   the reader's recent projects. A convenience: unreadable storage just means
   the row is seeded again. */
const OPEN_KEY = "salefish:spaces:open:v2";
function readOpen(): string[] | null {
  try {
    const v = JSON.parse(window.localStorage.getItem(OPEN_KEY) ?? "null");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string").slice(0, MAX_SPACES) : null;
  } catch {
    return null;
  }
}
function writeOpen(ids: string[]) {
  try {
    window.localStorage.setItem(OPEN_KEY, JSON.stringify(ids));
  } catch {
    /* private mode: the row still changes, it just is not remembered */
  }
}

/* How many times the reader has been shown the jump shortcut. Shown on the
   first two switches; using the shortcut once retires it. */
const HINT_KEY = "salefish:spaces:hint:v1";
const HINT_TIMES = 2;
function hintsShown(): number {
  try {
    return Number(window.localStorage.getItem(HINT_KEY) ?? "0") || 0;
  } catch {
    return HINT_TIMES;
  }
}
function setHintsShown(n: number) {
  try {
    window.localStorage.setItem(HINT_KEY, String(n));
  } catch {
    /* nothing to remember it in: the hint simply shows again */
  }
}

/* Company Settings and Platform Settings are spaces too, opened by entering
   them like a project, and each keeps the page the reader was last on in it.
   Per device. */
type SettingsArea = "company" | "platform";
const SETTINGS_KEY = "salefish:spaces:settings:v1";
const SETTINGS_HOME: Record<SettingsArea, string> = { company: "/company-admin", platform: "/admin" };
function readSettingsSpaces(): { open: SettingsArea[]; last: Partial<Record<SettingsArea, string>> } {
  try {
    const v = JSON.parse(window.localStorage.getItem(SETTINGS_KEY) ?? "{}");
    return { open: Array.isArray(v.open) ? v.open.filter((a: string) => a === "company" || a === "platform") : [], last: v.last && typeof v.last === "object" ? v.last : {} };
  } catch {
    return { open: [], last: {} };
  }
}
function writeSettingsSpaces(v: { open: SettingsArea[]; last: Partial<Record<SettingsArea, string>> }) {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(v));
  } catch {
    /* private mode: the space still shows while the area is open */
  }
}

/* A space moves along the row only. */
const alongTheRow: Modifier = ({ transform }) => ({ ...transform, y: 0 });

/** One space in the row: a project, or Company / Platform Settings while open. */
type Space =
  | { kind: "project"; id: string; label: string; project: any }
  | { kind: "settings"; id: SettingsArea; label: string; Icon: LucideIcon };

/**
 * Arc's spaces: a row at the foot of the menu, five at most and never more
 * than one line — projects, and Company or Platform Settings once entered.
 * Opening something not in the row puts it first and closes the fifth;
 * opening one already there leaves the row alone. Dragged (or, on touch,
 * held and moved) into the reader's own order, kept per device. A space
 * closes from its X, or from the menu a hold or right-click opens; closing the
 * one the reader is in opens the space to its left (Andrew, 2026-11-03). The
 * grid opens the full project list.
 */
export function ProjectSpaces({ currentProjectId, area = "project" }: { currentProjectId: string | null; area?: "project" | SettingsArea }) {
  const t = useTranslation();
  const [location, setLocation] = useLocation();
  const { user } = useAuth();
  const [settings, setSettings] = useState(readSettingsSpaces);
  const [openIds, setOpenIdsState] = useState<string[] | null>(readOpen);
  const setOpenIds = (next: string[]) => {
    setOpenIdsState(next);
    writeOpen(next);
  };

  // Every page in a settings area is remembered, so its space reopens there.
  useEffect(() => {
    if (area === "project") return;
    setSettings((prev) => {
      const next = { open: prev.open, last: { ...prev.last, [area]: location } };
      writeSettingsSpaces(next);
      return next;
    });
  }, [area, location]);

  /* A drag starts after 6px of movement, by mouse or finger (so a tap still
     opens the space and a held finger opens its menu), or with Space from the
     keyboard. */
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates, keyboardCodes: { start: ["Space"], cancel: ["Escape"], end: ["Space"] } }),
  );
  /* The device's copy of the list (lib/projectsSnapshot.ts) draws the row
     until the live list lands, so the spaces are in place from the first
     frame instead of being inserted ahead of the current one (they moved up
     to 108px on load). Drawn, never cached: the live list replaces it. */
  const { data: live } = useQuery(projectsListQuery(user?.role === "SUPER_ADMIN"));
  const snapshot = useMemo(() => (live ? null : readProjectsSnapshot(user?.id)?.projects ?? null), [live, user?.id]);
  const projects = (live ?? snapshot ?? []) as any[];
  const recent = useRecentProjects(projects as Array<{ id: string; projectName?: string }>, user?.id, MAX_SPACES);

  /* ONE row, never two (Andrew, 2026-11-02): no more spaces than the row's
     width holds beside the All projects button — a space is 30px plus a 4px
     gap — and never more than five. */
  // Measured whenever the row element appears, not once on mount: the row is
  // not drawn until the user is known, and a mount-time measure kept its
  // 200px guess for good, which holds four (2026-11-02).
  const [rowEl, rowRef] = useState<HTMLDivElement | null>(null);
  const [rowWidth, setRowWidth] = useState(202);
  useEffect(() => {
    if (!rowEl) return;
    const ro = new ResizeObserver(([e]) => setRowWidth(e.contentRect.width));
    ro.observe(rowEl);
    return () => ro.disconnect();
  }, [rowEl]);
  // A space is 30px plus a 4px gap; the All projects button keeps 32. The
  // docked menu's 202px row holds five (Andrew, 2026-11-02: "there's room
  // for one more space"; at 32px a space it held four with 30px to spare).
  const room = Math.max(1, Math.min(MAX_SPACES, Math.floor((rowWidth - 32 + 0.5) / 34)));

  const SETTINGS_SPACES: Record<SettingsArea, { label: string; Icon: LucideIcon }> = {
    company: { label: t("companySettings", "Company Settings"), Icon: BriefcaseBusiness },
    platform: { label: t("platformSettings", "Platform Settings"), Icon: MonitorCog },
  };

  /* The ORDER is known too: useRecentProjects takes it from the server
     (`/api/users/me/recent-projects`), which answers after the list, so a
     first visit drew the current project alone and then slid it along as
     the recent ones landed in front of it (shift-audit chrome-load, 72 to
     108px). This device's own recent list stands in when it has one. */
  const recentQ = useQuery({ queryKey: ["/api/users/me/recent-projects"], enabled: false });
  const orderKnown = recentQ.isFetched || recentQ.isError || (!!user?.id && getRecentProjectIds(user.id).length > 0);
  const known = (!!live || !!snapshot) && orderKnown;
  const currentId = area === "project" ? currentProjectId : area;
  const projectById = useMemo(() => new Map((projects as any[]).map((p) => [p.id as string, p])), [projects]);

  // The first visit seeds the row from the reader's recent projects, the one
  // they are in first.
  useEffect(() => {
    if (!known || openIds) return;
    const seed = [...(currentId ? [currentId] : []), ...recent.map((p) => p.id)];
    setOpenIds([...new Set(seed)].slice(0, MAX_SPACES));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [known, openIds, currentId, recent]);

  // A project the reader can no longer open leaves the row once the live
  // list says so, rather than holding one of the five places.
  useEffect(() => {
    if (!live || !openIds) return;
    const kept = openIds.filter((id) => id === "company" || id === "platform" || projectById.has(id));
    if (kept.length !== openIds.length) setOpenIds(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, openIds, projectById]);

  // The space just closed while the reader was in it: until the route has
  // moved on, it must not come straight back as "something newly opened".
  const closedHere = useRef<string | null>(null);
  useEffect(() => {
    if (closedHere.current && currentId !== closedHere.current) closedHere.current = null;
  }, [currentId]);

  // Opening something not in the row puts it first and closes the fifth.
  useEffect(() => {
    if (!openIds || !currentId || openIds.includes(currentId)) return;
    if (currentId === closedHere.current) return;
    if (area === "project" && !projectById.has(currentId)) return;
    setOpenIds([currentId, ...openIds].slice(0, MAX_SPACES));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openIds, currentId, area, projectById]);

  const spaces = useMemo<Space[]>(() => {
    // Nothing to place them by yet (a first visit): an empty row of the same
    // height, then every space at once.
    if (!known || !openIds) return [];
    const all = openIds.flatMap<Space>((id) => {
      if (id === "company" || id === "platform") return [{ kind: "settings", id, ...SETTINGS_SPACES[id] }];
      const p = projectById.get(id);
      return p ? [{ kind: "project", id, label: p.projectName ?? "", project: p }] : [];
    });
    return all.slice(0, room);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [known, openIds, projectById, room, t]);

  const spacesRef = useRef(spaces);
  spacesRef.current = spaces;
  const hrefOf = (s: Space) => (s.kind === "project" ? `/projects/${s.id}/dashboard` : settings.last[s.id] ?? SETTINGS_HOME[s.id]);
  const hrefRef = useRef(hrefOf);
  hrefRef.current = hrefOf;

  const close = (s: Space) => {
    setOpenIds((openIds ?? []).filter((id) => id !== s.id));
    if (s.id !== currentId) return;
    // Closing the space you are in opens the one to its left (the next one
    // if it was first), as closing a tab does; the last one leaves you on
    // the project list.
    const row = spacesRef.current;
    const at = row.findIndex((x) => x.id === s.id);
    const next = row[at - 1] ?? row[at + 1];
    closedHere.current = s.id;
    setLocation(next ? hrefRef.current(next) : "/projects");
  };

  /* ⌃1–⌃5 (Alt+1–5 off Apple) jump to the spaces in the row's order. */
  const viaKey = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const held = isApplePlatform() ? e.ctrlKey && !e.metaKey && !e.altKey : e.altKey && !e.ctrlKey && !e.metaKey;
      const digit = held ? /^Digit([1-5])$/.exec(e.code) : null;
      const target = digit ? spacesRef.current[Number(digit[1]) - 1] : null;
      if (!target) return;
      e.preventDefault();
      viaKey.current = true;
      setHintsShown(HINT_TIMES);
      setLocation(hrefRef.current(target));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setLocation]);

  /* Switched project by any other way (a click, ⌘K, the project list): the
     first couple of times, say which key would have done it. */
  const lastProject = useRef(currentProjectId);
  useEffect(() => {
    const before = lastProject.current;
    lastProject.current = currentProjectId;
    if (!currentProjectId || !before || before === currentProjectId) return;
    if (viaKey.current) {
      viaKey.current = false;
      return;
    }
    const at = spacesRef.current.findIndex((s) => s.id === currentProjectId);
    const shown = hintsShown();
    if (at < 0 || shown >= HINT_TIMES) return;
    setHintsShown(shown + 1);
    toast({
      title: t("shellSpaceHintTitle", "Tip: {key} jumps here", { key: spaceShortcut(at + 1) }),
      description: t("shellSpaceHintBody", "Each space in the menu has its own number, left to right."),
    });
  }, [currentProjectId, t]);

  // Moves a space to where another one is, by id: the row drawn can be
  // shorter than the list kept.
  const reorder = (id: string, toId: string) => {
    const ids = openIds ?? [];
    const from = ids.indexOf(id), to = ids.indexOf(toId);
    if (from < 0 || to < 0 || from === to) return;
    setOpenIds(arrayMove(ids, from, to));
  };
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (over && active.id !== over.id) reorder(String(active.id), String(over.id));
  };

  return (
    <div ref={rowRef} className="flex flex-nowrap items-center gap-1 overflow-hidden px-2 pb-3 pt-1.5" role="group" aria-label={t("shellSpaces", "Spaces")}>
      <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[alongTheRow]} onDragEnd={onDragEnd}>
        <SortableContext items={spaces.map((s) => s.id)} strategy={horizontalListSortingStrategy}>
          {spaces.map((s, i) => {
            const on = s.kind === "project" ? s.id === currentProjectId : area === s.id;
            return (
              <SortableSpace
                key={s.id}
                id={s.id}
                label={s.label}
                shortcut={spaceShortcut(i + 1)}
                on={on}
                onOpen={() => setLocation(hrefOf(s))}
                onClose={() => close(s)}
                onMoveLeft={i > 0 ? () => reorder(s.id, spaces[i - 1].id) : undefined}
                onMoveRight={i < spaces.length - 1 ? () => reorder(s.id, spaces[i + 1].id) : undefined}
                closeLabel={t("shellSpaceClose", "Close {project}", { project: s.label })}
              >
                {s.kind === "project"
                  ? <LogoDisc name={s.label} src={s.project.projectLogo ?? null} size={26} />
                  : <span className="flex size-[26px] items-center justify-center rounded-full bg-[hsl(var(--menu-wash)/0.12)] text-sidebar-strong"><s.Icon className="size-4" aria-hidden="true" /></span>}
              </SortableSpace>
            );
          })}
        </SortableContext>
      </DndContext>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => setLocation("/projects")}
            aria-label={t("shellSpacesAll", "All Projects")}
            data-testid="space-all"
            className="hit-target ml-auto flex size-8 shrink-0 items-center justify-center rounded-full text-sidebar-muted outline-hidden transition-colors duration-instant ease-standard hover:bg-[hsl(var(--menu-wash)/0.08)] hover:text-sidebar-strong focus-visible:ring-2 focus-visible:ring-sidebar-on-active"
          >
            <LayoutGrid className="size-[18px]" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{t("shellSpacesAll", "All Projects")}</TooltipContent>
      </Tooltip>
    </div>
  );
}

/** How long a finger holds still on a space before its menu opens. */
const HOLD_MS = 450;

/**
 * One space: opens on a click, drags along the row, closes from its X. On a
 * touch screen, where there is no hover to show the X, holding a space opens
 * a menu to move it or close it (Andrew, 2026-11-02); a right-click opens the
 * same menu with a mouse.
 */
function SortableSpace({ id, label, shortcut, on, onOpen, onClose, onMoveLeft, onMoveRight, closeLabel, children }: { id: string; label: string; shortcut: string; on: boolean; onOpen: () => void; onClose: () => void; onMoveLeft?: () => void; onMoveRight?: () => void; closeLabel: string; children: React.ReactNode }) {
  const t = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const [menuOpen, setMenuOpen] = useState(false);
  const hold = useRef<{ timer: number; x: number; y: number } | null>(null);
  const opened = useRef(false);
  const endHold = () => {
    if (hold.current) window.clearTimeout(hold.current.timer);
    hold.current = null;
  };
  useEffect(() => endHold, []);
  useEffect(() => {
    if (isDragging) endHold();
  }, [isDragging]);
  return (
    <div
      ref={setNodeRef}
      className={`group/space relative ${isDragging ? "z-raised" : ""}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            {...attributes}
            {...listeners}
            onPointerDown={(e) => {
              listeners?.onPointerDown?.(e);
              opened.current = false;
              if (e.pointerType === "mouse") return;
              endHold();
              hold.current = {
                x: e.clientX,
                y: e.clientY,
                timer: window.setTimeout(() => {
                  hold.current = null;
                  opened.current = true;
                  setMenuOpen(true);
                }, HOLD_MS),
              };
            }}
            onPointerMove={(e) => {
              if (hold.current && Math.hypot(e.clientX - hold.current.x, e.clientY - hold.current.y) > 8) endHold();
            }}
            onPointerUp={endHold}
            onPointerCancel={endHold}
            onContextMenu={(e) => {
              e.preventDefault();
              endHold();
              opened.current = true;
              setMenuOpen(true);
            }}
            onClick={() => {
              // The tap that ended a hold opened the menu, not the space.
              if (opened.current) {
                opened.current = false;
                return;
              }
              onOpen();
            }}
            aria-label={label}
            aria-current={on ? "true" : undefined}
            data-testid={`space-${id}`}
            className={`hit-target relative flex size-[30px] shrink-0 touch-none select-none items-center justify-center rounded-full outline-hidden transition-transform duration-instant ease-standard [-webkit-touch-callout:none] focus-visible:ring-2 focus-visible:ring-sidebar-on-active ${isDragging ? "scale-110 cursor-grabbing shadow-lg" : "hover:scale-[1.06]"} ${on ? "shadow-[0_0_0_2px_hsl(var(--sidebar-on-active))]" : isDragging ? "" : "opacity-80 hover:opacity-100"}`}
          >
            {children}
          </button>
        </TooltipTrigger>
        {!isDragging && !menuOpen && (
          <TooltipContent side="top">
            {label}
            <kbd className="ml-2 font-sans opacity-70">{shortcut}</kbd>
          </TooltipContent>
        )}
      </Tooltip>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        {/* An anchor only: the space itself opens the menu by a hold or a
            right-click, never by a press, which opens the space. */}
        <DropdownMenuTrigger asChild>
          <span aria-hidden="true" tabIndex={-1} className="pointer-events-none absolute inset-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="top" align="start" data-testid={`space-menu-${id}`}>
          {onMoveLeft && (
            <DropdownMenuItem onSelect={onMoveLeft} data-testid={`space-move-left-${id}`}>
              <ArrowLeft aria-hidden="true" />
              {t("shellSpaceMoveLeft", "Move Left")}
            </DropdownMenuItem>
          )}
          {onMoveRight && (
            <DropdownMenuItem onSelect={onMoveRight} data-testid={`space-move-right-${id}`}>
              <ArrowRight aria-hidden="true" />
              {t("shellSpaceMoveRight", "Move Right")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={onClose} data-testid={`space-menu-close-${id}`}>
            <X aria-hidden="true" />
            {t("shellSpaceCloseShort", "Close")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {!isDragging && (
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          data-testid={`space-close-${id}`}
          /* Not on a touch screen: a 16px corner cannot grow to a 44px target
             without taking the taps meant for the space under it. Holding the
             space opens its menu there instead. */
          className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-sidebar-foreground text-[var(--ground-top)] opacity-0 shadow-sm outline-hidden transition-opacity duration-instant ease-standard focus-visible:opacity-100 group-hover/space:opacity-100 coarse:hidden"
        >
          <X className="size-2.5" strokeWidth={3} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
